import type { LocalFile } from '@frontend/api-types';

// "System" is used for events that are not directly user-initiated, e.g. a background process
type Action = 'Create' | 'Delete' | 'Dismiss' | 'Select' | 'System' | 'Update' | 'View';

type TaskFlowBaseEventData = {
  currentActiveTasks?: MaybeNull<number>;
  entityId?: string;
  entityType?: string;
  individualMaxActiveTasks?: MaybeNull<number>;
  planMaxActiveTasks?: MaybeNull<number>;
  uniqueKey: string;
};

type ErrorCategory = 'auth' | 'network' | 'payment' | 'stripe' | 'timeout' | 'unknown' | 'validation';

type FlowVariant = 'frictionless' | 'legacy' | 'mcp' | 'v2';

type NormalizedAnalyticsError = {
  backendCode?: number;
  errorCategory: ErrorCategory;
  errorCode: string;
  errorSource: string;
  httpStatus?: number;
  retriable: boolean;
};

type PaywallPurchaseType = 'ALaCarteCredits' | 'Subscription';

export type SignUpBaseEventData = {
  entryType: 'accept-invite' | 'frictionless' | 'mcp' | 'signup';
  flowVariant: FlowVariant;
  platform?: 'mobile' | 'web';
};

type SignupMethod = 'email' | 'sso_apple' | 'sso_google';

type SsoCallbackOutcome = 'authenticated' | 'error' | 'missing_requirements' | 'timeout';

type SsoProvider = 'apple' | 'google';

type OnboardingMembershipPlatform = 'mobile' | 'web';
type OnboardingMembershipStep = 'complete' | 'paymentCapture' | 'selectPlan';

const makeEvent = <TData extends GenericRecord = never>(config: { action: Action; object: string }) =>
  // Small hack to avoid having to provide a real runtime dummy value
  config as {
    action: Action;
    data: TData;
    /** Object refers to the element interacted with, not a JS object */
    object: string;
  };

export type DraftTaskEntryPoint = 'home' | 'im_feeling_ducky' | 'new_task_page' | 'unknown';

export type TaskCreationEntryPoint =
  | 'bottom_nav_cta'
  | 'command_palette'
  | 'home_input'
  | 'im_feeling_ducky'
  | 'sidebar_cta';

export const analyticsMap = {
  auth: {
    deleteClientConfirmed: makeEvent<{
      clientId: string;
    }>({
      action: 'Delete',
      object: 'Delete Account Button',
    }),
    experimentLandingPageViewed: makeEvent<{
      hasTaskDescription: boolean;
      platform: 'mobile' | 'web';
    }>({
      action: 'View',
      object: 'Experiment Landing Page',
    }),
    experimentSignupCompleted: makeEvent<
      SignUpBaseEventData & {
        hasTaskDescription: boolean;
        method: SignupMethod;
        utmCampaign?: string;
        utmContent?: string;
        utmMedium?: string;
        utmSource?: string;
      }
    >({
      action: 'Create',
      object: 'Experiment Signup Completed',
    }),
    experimentSignupMethodSelected: makeEvent<
      SignUpBaseEventData & {
        method: SignupMethod;
      }
    >({
      action: 'Select',
      object: 'Experiment Signup Method',
    }),
    experimentSignupStarted: makeEvent<
      SignUpBaseEventData & {
        hasTaskDescription: boolean;
        method: SignupMethod;
      }
    >({
      action: 'Select',
      object: 'Experiment Signup',
    }),
    experimentTaskEntered: makeEvent<{
      taskDescriptionLength: number;
    }>({
      action: 'Create',
      object: 'Experiment Task',
    }),
    login: makeEvent({
      action: 'Select',
      object: 'Login Button',
    }),
    logout: makeEvent({
      action: 'Select',
      object: 'Logout Button',
    }),
    mcpAddPaymentMethodSelected: makeEvent<SignUpBaseEventData>({
      action: 'Select',
      object: 'MCP Add Payment Method Button',
    }),
    mcpProfileCompletionFailed: makeEvent<NormalizedAnalyticsError & SignUpBaseEventData>({
      action: 'System',
      object: 'MCP Profile Completion',
    }),
    mcpProfileCompletionRequired: makeEvent<
      SignUpBaseEventData & {
        needsBirthday: boolean;
        needsPhone: boolean;
      }
    >({
      action: 'System',
      object: 'MCP Profile Completion',
    }),
    mcpProfileCompletionStarted: makeEvent<SignUpBaseEventData>({
      action: 'Select',
      object: 'MCP Profile Completion Button',
    }),
    mcpProfileCompletionSucceeded: makeEvent<SignUpBaseEventData>({
      action: 'Create',
      object: 'MCP Profile Completion',
    }),
    mcpSuccessRetrySelected: makeEvent<NormalizedAnalyticsError & SignUpBaseEventData>({
      action: 'Select',
      object: 'MCP Success Retry Button',
    }),
    mcpSuccessSignedOut: makeEvent<SignUpBaseEventData>({
      action: 'Select',
      object: 'MCP Success Sign Out Button',
    }),
    mcpSuccessViewed: makeEvent<SignUpBaseEventData>({
      action: 'View',
      object: 'MCP Success',
    }),
    onboardingLogout: makeEvent<{ step: 'complete' | 'membership' | 'profile' }>({
      action: 'Select',
      object: 'Onboarding Logout Button',
    }),
    signupCompleted: makeEvent<{
      hasDescription?: boolean;
      hasPromoCode: boolean;
      hasReferrer: boolean;
      hasUtmParams: boolean;
      utmCampaign?: string;
      utmContent?: string;
      utmMedium?: string;
      utmSource?: string;
      utmTerm?: string;
    }>({
      action: 'Create',
      object: 'Signup Completed',
    }),
    signupLandingViewed: makeEvent<
      SignUpBaseEventData & {
        hasInviteId: boolean;
        hasPromoCode: boolean;
      }
    >({
      action: 'View',
      object: 'Signup Landing',
    }),
    signupMethodSelected: makeEvent<
      SignUpBaseEventData & {
        hasInviteId: boolean;
        hasPromoCode: boolean;
        method: SignupMethod;
      }
    >({
      action: 'Select',
      object: 'Signup Method',
    }),
    signupPromoCodeApplied: makeEvent<
      SignUpBaseEventData & {
        isHouseholdInvite: boolean;
      }
    >({
      action: 'System',
      object: 'Signup Promo Code',
    }),
    signupPromoCodeRemoved: makeEvent<
      SignUpBaseEventData & {
        isHouseholdInvite: boolean;
      }
    >({
      action: 'Update',
      object: 'Signup Promo Code',
    }),
    signupPromoCodeSubmitFailed: makeEvent<
      NormalizedAnalyticsError &
        SignUpBaseEventData & {
          isHouseholdInvite: boolean;
        }
    >({
      action: 'System',
      object: 'Signup Promo Code',
    }),
    signupStepViewed: makeEvent<
      SignUpBaseEventData & {
        step: string;
      }
    >({
      action: 'View',
      object: 'Signup Step',
    }),
    signupSubmitFailed: makeEvent<
      NormalizedAnalyticsError &
        SignUpBaseEventData & {
          hasPromoCode: boolean;
        }
    >({
      action: 'System',
      object: 'Signup Submit',
    }),
    signupSubmitStarted: makeEvent<
      SignUpBaseEventData & {
        hasPromoCode: boolean;
      }
    >({
      action: 'Select',
      object: 'Signup Submit Button',
    }),
    signupSubmitSucceeded: makeEvent<
      SignUpBaseEventData & {
        hasPromoCode: boolean;
      }
    >({
      action: 'Create',
      object: 'Signup Submit',
    }),
    ssoCallbackError: makeEvent<NormalizedAnalyticsError & SignUpBaseEventData>({
      action: 'System',
      object: 'SSO Callback',
    }),
    ssoCallbackOutcome: makeEvent<
      SignUpBaseEventData & {
        isNewSignUp: boolean;
        outcome: SsoCallbackOutcome;
      }
    >({
      action: 'System',
      object: 'SSO Callback',
    }),
    ssoCallbackTimeout: makeEvent<SignUpBaseEventData>({
      action: 'System',
      object: 'SSO Callback',
    }),
    ssoCallbackViewed: makeEvent<SignUpBaseEventData>({
      action: 'View',
      object: 'SSO Callback',
    }),
    ssoCancel: makeEvent<
      SignUpBaseEventData & {
        provider: SsoProvider;
      }
    >({
      action: 'Dismiss',
      object: 'Signup SSO',
    }),
    ssoFail: makeEvent<
      NormalizedAnalyticsError &
        SignUpBaseEventData & {
          provider?: SsoProvider;
        }
    >({
      action: 'System',
      object: 'Signup SSO',
    }),
    ssoMissingRequirementsSubmitFailed: makeEvent<
      NormalizedAnalyticsError &
        SignUpBaseEventData & {
          missingFieldCount: number;
        }
    >({
      action: 'System',
      object: 'SSO Complete Profile',
    }),
    ssoMissingRequirementsSubmitStarted: makeEvent<
      SignUpBaseEventData & {
        missingFieldCount: number;
      }
    >({
      action: 'Select',
      object: 'SSO Complete Profile Button',
    }),
    ssoMissingRequirementsSubmitSucceeded: makeEvent<
      SignUpBaseEventData & {
        missingFieldCount: number;
      }
    >({
      action: 'Update',
      object: 'SSO Complete Profile',
    }),
    ssoSignupCompleted: makeEvent<
      SignUpBaseEventData & {
        hasReferrer?: boolean;
        hasUtmParams?: boolean;
        isNewSignUp: boolean;
        provider?: SsoProvider;
        utmCampaign?: string;
        utmContent?: string;
        utmMedium?: string;
        utmSource?: string;
        utmTerm?: string;
      }
    >({
      action: 'Create',
      object: 'SSO Signup Completed',
    }),
    ssoStart: makeEvent<
      SignUpBaseEventData & {
        provider: SsoProvider;
      }
    >({
      action: 'Select',
      object: 'Signup SSO Button',
    }),
    trialConversion: makeEvent<{
      planCode?: string;
    }>({
      action: 'Create',
      object: 'Trial Conversion',
    }),
  },
  commandPalette: {
    commandPaletteActionSelected: makeEvent<{ description: string; label: string }>({
      action: 'Select',
      object: 'Command Palette Action',
    }),
    commandPaletteOpened: makeEvent({
      action: 'View',
      object: 'Command Palette',
    }),
  },
  draftTasks: {
    addContextCalendarEventSelected: makeEvent<{ eventTitle: string }>({
      action: 'Select',
      object: 'Add Context Calendar Event',
    }),
    addContextContactSelected: makeEvent<{ contactName: string }>({
      action: 'Select',
      object: 'Add Context Contact',
    }),
    addContextReminderSelected: makeEvent<{ reminderTitle: string }>({
      action: 'Select',
      object: 'Add Context Reminder',
    }),
    deleteDraftTaskButtonSelected: makeEvent<{ draftTaskId: string }>({
      action: 'Select',
      object: 'Delete Draft Task Button',
    }),
    draftTaskCheckoutCompleted: makeEvent<{ entryPoint: DraftTaskEntryPoint }>({
      action: 'Create',
      object: 'Task From Draft',
    }),
    draftTaskCreated: makeEvent<{ entryPoint: DraftTaskEntryPoint }>({
      action: 'Create',
      object: 'Draft Task',
    }),
    imFeelingDuckySelected: makeEvent({
      action: 'Select',
      object: "I'm Feeling Ducky Button",
    }),
    taskCreationStarted: makeEvent<{ entryPoint: TaskCreationEntryPoint }>({
      action: 'Select',
      object: 'Create Task CTA',
    }),
    updateDraftTaskSubmitButtonSelected: makeEvent<
      TaskFlowBaseEventData & {
        attachments?: LocalFile[];
        description: string;
        draftTaskId: string;
        name: string;
        nextSteps: MaybeUndefined<string>;
      }
    >({
      action: 'Select',
      object: 'Update Draft Task Submit Button',
    }),
  },
  examples: {
    directoryBannerSelected: makeEvent({
      action: 'Select',
      object: 'Duckbill Directory Banner',
    }),
    exampleSelected: makeEvent<{ exampleId: string; exampleType?: string; taskName: string }>({
      action: 'Select',
      object: 'Example Button',
    }),
    exampleViewed: makeEvent<{ exampleId: string; exampleType?: string; taskName: string }>({
      action: 'View',
      object: 'Example',
    }),
    exploreDirectorySelected: makeEvent({
      action: 'Select',
      object: 'Explore Directory Button',
    }),
  },
  gifts: {
    bannerSelected: makeEvent({
      action: 'Select',
      object: 'Gift a Task Banner',
    }),
    giftSubmitted: makeEvent<{ hasDescription: boolean }>({
      action: 'Create',
      object: 'Gift a Task Submit Button',
    }),
    giftSucceeded: makeEvent<{ hasDescription: boolean }>({
      action: 'System',
      object: 'Gift a Task Success',
    }),
  },
  invite: {
    inviteModalOpened: makeEvent({
      action: 'View',
      object: 'Invite Modal',
    }),
    referralLinkCopied: makeEvent({
      action: 'View',
      object: 'Referral Link Copied',
    }),
  },
  navigation: {
    tabBarIconSelected: makeEvent<{
      label: MaybeUndefined<string>;
      params: MaybeUndefined<GenericRecord>;
      route: MaybeUndefined<string>;
    }>({
      action: 'Select',
      object: 'Tab Bar Icon',
    }),
  },
  onboarding: {
    androidDownloadLinkSelected: makeEvent({
      action: 'Select',
      object: 'Android Download Link',
    }),
    iosDownloadLinkSelected: makeEvent({
      action: 'Select',
      object: 'IOS Download Link',
    }),
    membershipApplePayCardAdded: makeEvent<{
      onboardingSessionId?: string;
      planCode?: string;
      platform: OnboardingMembershipPlatform;
      purchaseType: 'Subscription';
      step: OnboardingMembershipStep;
    }>({
      action: 'Create',
      object: 'Onboarding Membership Apple Pay Card',
    }),
    membershipCompleteCTASelected: makeEvent<{
      onboardingSessionId?: string;
      planCode?: string;
      platform: OnboardingMembershipPlatform;
      purchaseType?: 'Subscription';
      step?: OnboardingMembershipStep;
    }>({
      action: 'Select',
      object: 'Onboarding Membership Complete Button',
    }),
    membershipContinueToPaymentSelected: makeEvent<{
      onboardingSessionId?: string;
      planCode?: string;
      platform: OnboardingMembershipPlatform;
      purchaseType: 'Subscription';
      step: OnboardingMembershipStep;
    }>({
      action: 'Select',
      object: 'Onboarding Membership Continue Button',
    }),
    membershipCreditCardEntryCompleted: makeEvent<{
      onboardingSessionId?: string;
      planCode?: string;
      platform: OnboardingMembershipPlatform;
      purchaseType: 'Subscription';
      step: OnboardingMembershipStep;
    }>({
      action: 'Update',
      object: 'Onboarding Membership Credit Card Form',
    }),
    membershipCreditCardEntryStarted: makeEvent<{
      onboardingSessionId?: string;
      planCode?: string;
      platform: OnboardingMembershipPlatform;
      purchaseType: 'Subscription';
      step: OnboardingMembershipStep;
    }>({
      action: 'Update',
      object: 'Onboarding Membership Credit Card Form',
    }),
    membershipPaymentSubmitAttempted: makeEvent<{
      onboardingSessionId?: string;
      planCode?: string;
      platform: OnboardingMembershipPlatform;
      purchaseType: 'Subscription';
      step: OnboardingMembershipStep;
    }>({
      action: 'Select',
      object: 'Onboarding Membership Payment Submit Button',
    }),
    membershipPaymentSubmitFailed: makeEvent<
      NormalizedAnalyticsError & {
        onboardingSessionId?: string;
        planCode?: string;
        platform: OnboardingMembershipPlatform;
        purchaseType: 'Subscription';
        step: OnboardingMembershipStep;
      }
    >({
      action: 'System',
      object: 'Onboarding Membership Payment Submission',
    }),
    membershipPaymentSubmitSucceeded: makeEvent<{
      onboardingSessionId?: string;
      planCode?: string;
      platform: OnboardingMembershipPlatform;
      purchaseType: 'Subscription';
      step: OnboardingMembershipStep;
    }>({
      action: 'System',
      object: 'Onboarding Membership Payment Submission',
    }),
    membershipPlanSelected: makeEvent<{
      onboardingSessionId?: string;
      planCode?: string;
      platform: OnboardingMembershipPlatform;
      purchaseType: 'Subscription';
      step: OnboardingMembershipStep;
    }>({
      action: 'Select',
      object: 'Onboarding Membership Plan',
    }),
    membershipPromoCodeApplied: makeEvent<{
      onboardingSessionId?: string;
      planCode?: string;
      platform: OnboardingMembershipPlatform;
      purchaseType: 'Subscription';
      step: OnboardingMembershipStep;
    }>({
      action: 'System',
      object: 'Onboarding Membership Promo Code',
    }),
    membershipPromoCodeRemoved: makeEvent<{
      onboardingSessionId?: string;
      planCode?: string;
      platform: OnboardingMembershipPlatform;
      purchaseType: 'Subscription';
      step: OnboardingMembershipStep;
    }>({
      action: 'Update',
      object: 'Onboarding Membership Promo Code',
    }),
    membershipPromoCodeSubmitFailed: makeEvent<
      NormalizedAnalyticsError & {
        onboardingSessionId?: string;
        planCode?: string;
        platform: OnboardingMembershipPlatform;
        purchaseType: 'Subscription';
        step: OnboardingMembershipStep;
      }
    >({
      action: 'System',
      object: 'Onboarding Membership Promo Code',
    }),
    membershipPurchaseCompleted: makeEvent<{
      amountCharged?: number;
      onboardingSessionId?: string;
      planCode?: string;
      planName?: string;
      planPrice?: number;
      platform: OnboardingMembershipPlatform;
      purchaseType: 'Subscription';
      step: OnboardingMembershipStep;
    }>({
      action: 'Create',
      object: 'Onboarding Membership Purchase Completed',
    }),
    membershipStepViewed: makeEvent<{
      onboardingSessionId?: string;
      planCode?: string;
      platform: OnboardingMembershipPlatform;
      purchaseType?: 'Subscription';
      step: OnboardingMembershipStep;
    }>({
      action: 'View',
      object: 'Onboarding Membership Step',
    }),
    scheduleACallSelected: makeEvent({
      action: 'Select',
      object: 'Schedule A Call',
    }),
  },
  options: {
    deselectedOption: makeEvent<{ optionId: string; sheetId: string }>({
      action: 'Select',
      object: 'Option Deselect',
    }),
    reviewSelectedOptions: makeEvent<{ sheetId: string }>({
      action: 'Select',
      object: 'Sheet Review Button',
    }),
    selectedOption: makeEvent<{ optionId: string; sheetId: string }>({
      action: 'Select',
      object: 'Option Select',
    }),
    submitSelectedOptions: makeEvent<{ sheetId: string }>({
      action: 'Select',
      object: 'Sheet Submit Review Button',
    }),
    viewedOption: makeEvent<{ optionId: string; sheetId: string }>({
      action: 'View',
      object: 'Option',
    }),
    viewedSheet: makeEvent<{ sheetId: string }>({
      action: 'View',
      object: 'Sheet',
    }),
  },
  paywall: {
    continueToPaymentSelected: makeEvent<{
      paywallSessionId?: string;
      planCode?: string;
      platform: 'web';
      purchaseType?: PaywallPurchaseType;
      step?: string;
    }>({
      action: 'Select',
      object: 'Continue To Payment Button',
    }),
    creditCardEntryCompleted: makeEvent<{
      paywallSessionId?: string;
      planCode?: string;
      platform: 'web';
      purchaseType: PaywallPurchaseType;
      step?: string;
    }>({
      action: 'Update',
      object: 'Credit Card Form',
    }),
    creditCardEntryStarted: makeEvent<{
      paywallSessionId?: string;
      planCode?: string;
      platform: 'web';
      purchaseType: PaywallPurchaseType;
      step?: string;
    }>({
      action: 'Update',
      object: 'Credit Card Form',
    }),
    dismissed: makeEvent<{
      paywallSessionId?: string;
      platform: 'web';
      reason: 'back' | 'close' | 'skip';
      step?: string;
    }>({
      action: 'Dismiss',
      object: 'Paywall',
    }),
    modalOpened: makeEvent({
      action: 'View',
      object: 'Paywall Modal',
    }),
    onboardingCompleteCTA: makeEvent<{
      paywallSessionId?: string;
      planCode?: string;
      platform: 'web';
      purchaseType?: PaywallPurchaseType;
    }>({
      action: 'Select',
      object: 'Welcome To Duckbill Button',
    }),
    paymentSubmitAttempted: makeEvent<{
      paywallSessionId?: string;
      planCode?: string;
      platform: 'mobile' | 'web';
      purchaseType: PaywallPurchaseType;
      step?: string;
    }>({
      action: 'Select',
      object: 'Payment Submit Button',
    }),
    paymentSubmitFailed: makeEvent<
      NormalizedAnalyticsError & {
        paywallSessionId?: string;
        planCode?: string;
        platform: 'mobile' | 'web';
        purchaseType: PaywallPurchaseType;
        step?: string;
      }
    >({
      action: 'System',
      object: 'Payment Submission',
    }),
    paymentSubmitSucceeded: makeEvent<{
      paywallSessionId?: string;
      planCode?: string;
      platform: 'mobile' | 'web';
      purchaseType: PaywallPurchaseType;
      step?: string;
    }>({
      action: 'System',
      object: 'Payment Submission',
    }),
    promoCodeApplied: makeEvent<{
      paywallSessionId?: string;
      planCode?: string;
      platform: 'web';
      purchaseType?: PaywallPurchaseType;
      step?: string;
    }>({
      action: 'System',
      object: 'Paywall Promo Code',
    }),
    promoCodeRemoved: makeEvent<{
      paywallSessionId?: string;
      platform: 'web';
      purchaseType?: PaywallPurchaseType;
      step?: string;
    }>({
      action: 'Update',
      object: 'Paywall Promo Code',
    }),
    promoCodeSubmitFailed: makeEvent<
      NormalizedAnalyticsError & {
        paywallSessionId?: string;
        platform: 'web';
        purchaseType?: PaywallPurchaseType;
        step?: string;
      }
    >({
      action: 'System',
      object: 'Paywall Promo Code',
    }),
    purchaseCompleted: makeEvent<{
      creditAmount?: number;
      planCode?: string;
      planName?: string;
      purchaseType: PaywallPurchaseType;
      totalPrice?: number;
    }>({
      action: 'Create',
      object: 'Purchase Completed',
    }),
    purchaseTypeSelected: makeEvent<{
      paywallSessionId?: string;
      platform: 'web';
      purchaseType: PaywallPurchaseType;
      step?: string;
    }>({
      action: 'Select',
      object: 'Purchase Type',
    }),
    stepViewed: makeEvent<{
      paywallSessionId?: string;
      platform: 'web';
      purchaseType?: PaywallPurchaseType;
      step: string;
    }>({
      action: 'View',
      object: 'Paywall Step',
    }),
    subscriptionPlanSelected: makeEvent<{
      paywallSessionId?: string;
      planCode?: string;
      platform: 'web';
      purchaseType?: PaywallPurchaseType;
      step?: string;
    }>({
      action: 'Select',
      object: 'Subscription Plan',
    }),
  },
  previewTask: {
    loginClicked: makeEvent({
      action: 'Select',
      object: 'Preview Login Button',
    }),
    signupClicked: makeEvent({
      action: 'Select',
      object: 'Preview Signup Button',
    }),
    viewFailed: makeEvent<{ encodedId: string }>({
      action: 'View',
      object: 'Preview Task Failed',
    }),
    viewed: makeEvent<{ hasQueryParams: boolean; taskId: string }>({
      action: 'View',
      object: 'Preview Task',
    }),
  },
  recurrences: {
    deleteRecurrenceButtonSelected: makeEvent<{ recurrenceId: string }>({
      action: 'Select',
      object: 'Delete Repeating Task Button',
    }),
    selectedFindRepeatingTasksBanner: makeEvent({
      action: 'Select',
      object: 'Find Repeating Tasks Banner',
    }),
  },
  secureNote: {
    secureNoteDeleted: makeEvent<{ secureNoteId: string }>({
      action: 'Delete',
      object: 'Secure Note',
    }),
    secureNoteUpdated: makeEvent<{ secureNoteId: string }>({
      action: 'Update',
      object: 'Secure Note',
    }),
    secureNoteViewed: makeEvent<{ secureNoteId: string }>({
      action: 'View',
      object: 'Secure Note',
    }),
  },
  shareTask: {
    emailAdded: makeEvent<{ emailCount: number; taskId: string }>({
      action: 'Update',
      object: 'Share Task Email',
    }),
    emailRemoved: makeEvent<{ emailCount: number; taskId: string }>({
      action: 'Delete',
      object: 'Share Task Email',
    }),
    linkCopied: makeEvent<{ taskId: string }>({
      action: 'Select',
      object: 'Share Task Link Copy',
    }),
    linkCreated: makeEvent<{ emailCount: number; emails: string[]; taskId: string }>({
      action: 'Create',
      object: 'Share Task Link',
    }),
    modalOpened: makeEvent<{ taskId: string }>({
      action: 'View',
      object: 'Share Task Modal',
    }),
  },
  stateMachine: {
    stateTransition: makeEvent<{ context: Record<string, unknown>; fromState: string; toState: string }>({
      action: 'View',
      object: 'State Transition',
    }),
  },
  suggestions: {
    suggestionDismissed: makeEvent<{ suggestionId: string }>({
      action: 'Dismiss',
      object: 'Home Task Suggestion',
    }),
    suggestionSelected: makeEvent<{ suggestion: string; suggestionId?: string }>({
      action: 'Select',
      object: 'Home Task Suggestion',
    }),
  },
  taskGameplanSuggestions: {
    suggestionBlockDismissed: makeEvent<{ commentId: string; suggestionId: string; taskId: string }>({
      action: 'Dismiss',
      object: 'Task Gameplan Suggestion Block',
    }),
    suggestionBlockShown: makeEvent<{ commentId: string; optionIds: string[]; taskId: string }>({
      action: 'View',
      object: 'Task Gameplan Suggestion Block',
    }),
    suggestionOptionClicked: makeEvent<{ commentId: string; optionId: string; taskId: string }>({
      action: 'Select',
      object: 'Task Gameplan Suggestion Option',
    }),
  },
  taskProactiveOffer: {
    suggestionBlockDismissed: makeEvent<{ commentId: string; suggestionId: string; taskId: string }>({
      action: 'Dismiss',
      object: 'Proactive Task Offer Block',
    }),
    suggestionBlockShown: makeEvent<{ commentId: string; optionIds: string[]; taskId: string }>({
      action: 'View',
      object: 'Proactive Task Offer Block',
    }),
    suggestionOptionClicked: makeEvent<{ commentId: string; optionId: string; taskId: string }>({
      action: 'Select',
      object: 'Proactive Task Offer Option',
    }),
  },
  tasks: {
    taskMessageSearchSelected: makeEvent<{ taskId: string }>({
      action: 'Select',
      object: 'Task Message Search',
    }),
    taskOverviewViewed: makeEvent<{ taskId: string }>({
      action: 'View',
      object: 'Task Overview',
    }),
    taskStatisticsCardViewed: makeEvent<{ taskId: string }>({
      action: 'View',
      object: 'Task Statistics Card',
    }),
  },
  waitlist: {
    waitlistSignupCompleted: makeEvent<{
      emailHash: string;
      messageId: string;
    }>({
      action: 'Create',
      object: 'Waitlist Signup Completed',
    }),
  },
  whispers: {
    whisperRendered: makeEvent<{
      commentId: string;
      source: 'plain' | 'rich';
      taskId: string;
      whisperType: string;
    }>({
      action: 'View',
      object: 'Whisper Rendered',
    }),
  },
} as const;

export type AnalyticsMap = typeof analyticsMap;

export type AnalyticsTopic = keyof AnalyticsMap;

/**
 * Topics consumable by `TaskChatSingleChoiceOptions` / `ChatSingleChoiceOptions`.
 * To add a new single-choice consumer: define a topic in `analyticsMap` with
 * `suggestionBlockShown` and `suggestionOptionClicked` events, add its key to
 * this union, then pass it as `analyticsTopic` from the caller.
 */
export type SingleChoiceAnalyticsTopic = 'taskGameplanSuggestions' | 'taskProactiveOffer';

export type AnalyticsEventPayload<
  Topic extends AnalyticsTopic,
  Event extends keyof AnalyticsMap[Topic],
> = AnalyticsMap[Topic][Event] extends { data: GenericRecord } ? AnalyticsMap[Topic][Event]['data'] : never;
